<template>
    <v-container fluid>
        <v-card class="mb-4">
            <v-card-title> Configure Material Samples </v-card-title>
            <v-card-text v-if="!configureSamplesContext.sampleSetId">
                Configure material samples below. Samples will not be created until you click
                "Create" at the bottom of the page.</v-card-text
            >
            <v-card-text v-else>
                Edit material samples below. Click "Save" to save changes.</v-card-text
            >
        </v-card>

        <v-card>
            <v-card-title>
                <v-row>
                    <v-col><span class="text-h6">Samples</span></v-col>
                    <v-spacer />
                    <v-col class="shrink text-right">
                        <v-btn
                            v-if="editingSampleList"
                            color="primary"
                            class="mr-2"
                            @click="removeSelectedSamples"
                            :disabled="!selectedItems.length"
                        >
                            <v-icon start>$delete</v-icon>
                            Remove
                        </v-btn>
                        <v-btn
                            v-if="editingSampleList"
                            color="primary"
                            class="mr-2"
                            @click="bulkEditSamples"
                            :disabled="!selectedItems.length"
                        >
                            <v-icon start>$pencil</v-icon>
                            Bulk Edit
                        </v-btn>
                        <v-btn
                            v-if="!editingSampleList"
                            color="primary"
                            class="mr-2"
                            @click="editingSampleList = true"
                        >
                            <v-icon start>$edit</v-icon>
                            Edit Samples
                        </v-btn>
                        <v-btn
                            v-if="editingSampleList"
                            color="primary"
                            class="mr-2"
                            @click="editingSampleList = false"
                        >
                            <v-icon start>$check</v-icon>
                            Done Editing
                        </v-btn>
                        <v-btn
                            v-if="configureSamplesContext.sampleSetId && !editingSampleList"
                            color="primary"
                            class="mr-2"
                            @click="downloadTemplate"
                            :disabled="!configureSamplesContext.sampleSetId"
                        >
                            <v-icon start>$download</v-icon>
                            Download Template
                        </v-btn>
                        <v-btn
                            v-if="configureSamplesContext.sampleSetId && !editingSampleList"
                            color="primary"
                            class="mr-2"
                            @click="showFileUploadDialog = true"
                            :disabled="!configureSamplesContext.sampleSetId"
                        >
                            <v-icon start>$upload</v-icon>
                            Replace from File
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text>
                <v-data-table
                    :headers="headers"
                    :items="samples"
                    class="elevation-1"
                    show-select
                    multi-select
                    return-object
                    v-model="selectedItems"
                >
                    <template v-slot:item.thingInstanceIds="{ item }">
                        {{ item.thingInstances.map((thing) => thing.name).join(", ") }}
                    </template>
                    <template v-slot:item.type="{ item }">
                        {{ configureSamplesContext.materialType?.name || item.type }}
                    </template>

                    <template v-slot:item.descriptor="{ item }">
                        <v-autocomplete
                            v-if="editingSampleList"
                            v-model="item.descriptor"
                            :items="sampleDescriptors"
                            item-title="name"
                            item-value="id"
                            hide-details
                            outline
                            clearable
                            variant="outlined"
                            density="compact"
                        ></v-autocomplete>
                        <span v-else>{{
                            sampleDescriptors.find((d) => d.id === item.descriptor)?.name ||
                            item.descriptor
                        }}</span>
                    </template>

                    <template v-slot:item.collectionDate="{ item }">
                        <v-date-input
                            v-if="editingSampleList"
                            v-model="item.collectionDate"
                            :rules="[(value) => !!value || 'Collection date is required']"
                        ></v-date-input>
                        <span v-else>{{ formatDate(item.collectionDate) }}</span>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-card class="mb-4 mt-4 justify-left" color="blue" theme="dark" height="100%">
            <v-card-title class="pb-1">
                <v-icon start>$test-tube</v-icon>
                <span class="text-h6">Sample Set Parameters</span>
            </v-card-title>

            <v-card-text class="text-body-1">
                <span
                    ><strong>Samples Collection SOP:&emsp;</strong
                    >{{ configureSamplesContext.collectionSOP?.name || "No SOP" }}</span
                >
                <br />
                <span
                    ><strong>Source Experiment:&emsp;</strong
                    >{{ configureSamplesContext.experimentCode }}</span
                >
            </v-card-text>
        </v-card>
        <v-row class="justify-end mb-4 mr-1">
            <v-btn color="primary" class="mr-2" @click="saveSamples" :disabled="!samples.length">
                {{ configureSamplesContext.sampleSetId ? "Save" : "Create" }}
            </v-btn>
            <v-btn color="primary" @click="cancel">Cancel</v-btn>
        </v-row>
        <v-dialog
            v-model="showFileUploadDialog"
            max-width="600px"
            @click:outside="closeFileUploadDialog"
            @keyup.escape="closeFileUploadDialog"
            persistent
            scrollable
            >'
            <v-card>
                <v-card-title>Replace Samples from File</v-card-title>
                <v-card-text>
                    <v-file-input
                        v-model="samplesFile"
                        label="Select file to upload"
                        accept=".xlsx"
                        variant="outlined"
                    ></v-file-input>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="uploadSamplesFromFile" :disabled="!samplesFile"
                        >Upload</v-btn
                    >
                    <v-btn @click="closeFileUploadDialog">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="bulkEditDialogVisible" max-width="600px" persistent scrollable>
            <material-samples-bulk-edit
                :samples="selectedItems"
                :sampleDescriptors="sampleDescriptors"
                @canceled="bulkEditDialogVisible = false"
                @samplesBulkEdited="handleBulkEditApplied"
            ></material-samples-bulk-edit>
        </v-dialog>
    </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import {
    MaterialSamplesContext,
    MaterialSample,
    samplesAPI,
    SaveMaterialSampleSetParams,
    SampleDescriptor,
} from "@/services/samples/samplesAPI";
import MaterialSamplesBulkEdit from "@/components/SamplesV2/MaterialSamplesBulkEdit.vue";
import { withLoadingScreen } from "@/lib/loading";
import { downloadFile } from "@/lib/downloadFileUtil";
import { useToast } from "vue-toastification";
import _ from "lodash";
import { DateTime } from "luxon";

const toast = useToast();

const { configureSamplesContext } = defineProps<{
    configureSamplesContext: MaterialSamplesContext;
}>();

const emit = defineEmits<{
    (e: "cancel"): void;
    (e: "saved"): void;
}>();

const materialTypeId = ref<string>("");
const samples = ref<MaterialSample[]>([]);
const headers = ref<VDataTableHeader[]>([
    { title: "Sample ID", value: "code", sortable: true, width: "15%" },
    { title: "Source Thing(s)", value: "thingInstanceIds", sortable: true, width: "20%" },
    { title: "Type", value: "type", sortable: true, width: "20%" },
    { title: "Descriptor", value: "descriptor", sortable: true, width: "25%" },
    { title: "Collection Date", value: "collectionDate", sortable: true, width: "25%" },
]);
const selectedItems = ref<MaterialSample[]>([]);
const bulkEditDialogVisible = ref(false);
const editingSampleList = ref(false);
const showFileUploadDialog = ref(false);
const samplesFile = ref<File | null>(null);
const sampleDescriptors = ref<SampleDescriptor[]>([]);

onMounted(() => {
    materialTypeId.value = configureSamplesContext.materialType!.id;
    sampleDescriptors.value = configureSamplesContext.descriptors;
    samples.value = configureSamplesContext.samples!.map((sample) => {
        return {
            id: sample.id,
            code: sample.code,
            thingInstances: sample.thingInstances,
            experimentCode: configureSamplesContext.experimentCode,
            type: sample.type,
            descriptor: sample.descriptor,
            collectionDate: sample.collectionDate,
        };
    });
});

function removeSelectedSamples() {
    selectedItems.value.forEach((item) => {
        const index = samples.value.indexOf(item);
        if (index > -1) {
            samples.value.splice(index, 1);
        }
    });
    selectedItems.value = [];
}

function bulkEditSamples() {
    bulkEditDialogVisible.value = true;
}

async function downloadTemplate() {
    await withLoadingScreen(async () => {
        const response = await samplesAPI.downloadSampleSetTemplate(
            configureSamplesContext.sampleSetId!
        );
        const filename = `Sample Set ${configureSamplesContext.sampleSetCode}.xlsx`;
        downloadFile(filename, response);
    });
}

async function uploadSamplesFromFile() {
    try {
        await withLoadingScreen(async () => {
            const result = await samplesAPI.uploadSamplesFromFile(
                configureSamplesContext.sampleSetId!,
                samplesFile.value!
            );

            if (result.invalidSamples) {
                const skippedMessage = result.invalidSamples
                    ? result.invalidSamples.map((msg) => `${msg}`).join("; ")
                    : "";
                toast.error(`Import skipped due to errors: ${skippedMessage}`);
            } else if (result.updatedSamples) {
                const updatedSamples = result.updatedSamples;
                updatedSamples.forEach((sample) => {
                    sample.collectionDate = new Date(sample.collectionDate!);
                });
                samples.value = updatedSamples;
                toast.success(`Successfully imported ${updatedSamples.length} samples.`);
            }
        });
    } catch (error) {
        const message = _.get(error, "response.data.error") || "Failed to upload sample details";
        toast.error(message);
    } finally {
        samplesFile.value = null;
        showFileUploadDialog.value = false;
    }
}

async function saveSamples() {
    withLoadingScreen(async () => {
        const sampleSopId =
            "id" in configureSamplesContext.collectionSOP!
                ? configureSamplesContext.collectionSOP.id
                : null;
        const payload = {
            samples: samples.value.map((sample) => ({
                ...sample,
                thingInstanceIds: sample.thingInstances.map((thing) => thing.id),
            })),
            sampleSopId,
            needByDate: configureSamplesContext.needByDate,
            sampleDescriptors: configureSamplesContext.descriptors.map(
                (descriptor) => descriptor.id
            ),
            materialTypeId: configureSamplesContext.materialType!.id,
        } as SaveMaterialSampleSetParams;

        await samplesAPI.updateSampleSet(configureSamplesContext.sampleSetId!, payload);
    });
    emit("saved");
}

function handleBulkEditApplied(editedFields: object) {
    // Update the selected samples with the edited fields
    selectedItems.value.forEach((item) => {
        Object.keys(editedFields).forEach((key) => {
            item[key] = editedFields[key];
        });
    });
    bulkEditDialogVisible.value = false;
}

function cancel() {
    // Close the editor
    emit("cancel");
}

function formatDate(date: Date | null): string | null {
    if (!date) {
        return null;
    }
    const luxonDate = DateTime.fromJSDate(date, { zone: "utc" });
    return luxonDate.toLocaleString(DateTime.DATE_MED);
}

function closeFileUploadDialog() {
    samplesFile.value = null;
    showFileUploadDialog.value = false;
}
</script>
